import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TextInput, 
  toast,
  DataTable,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge
} from '@etherisc/ui-kit';
import { 
  SearchIcon,
  AlertTriangleIcon,
  RefreshCwIcon,
  EyeIcon,
} from 'lucide-react';
import { NeutralButton, PositiveButton } from '../../components/buttons';
import { apiClient } from '../../services/api';

interface ClaimsEligibleQuote {
  id: string;
  quoteNumber: string;
  state: string;
  coverageStartDate: string;
  coverageEndDate: string;
  sumInsured: number;
  premiumAmount: number;
  participantCount: number;
  productName?: string;
  organizationName?: string;
  lastAssessmentDate?: string;
  hasAssessments: boolean;
}

interface ClaimsStatistics {
  assessments: {
    calculated: number;
    approved: number;
    paidOut: number;
  };
  quotesWithClaims: number;
  payoutAmounts: {
    calculated: number;
    approved: number;
    paidOut: number;
  };
}

export function ClaimsManagement() {
  const navigate = useNavigate();

  const handleView = (quote: ClaimsEligibleQuote) => {
    navigate(`/claims/${quote.id}`);
  };
  
  const [quotes, setQuotes] = useState<ClaimsEligibleQuote[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statistics, setStatistics] = useState<ClaimsStatistics | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  });

  const fetchQuotes = useCallback(async (page = 1, search = '') => {
    try {
      setLoading(true);
      const data = await apiClient.getClaimsEligibleQuotes({
        page,
        limit: pagination.limit,
        search,
      });
      setQuotes(data.quotes || []);
      setPagination(data.pagination || pagination);
    } catch (error) {
      console.error('Error fetching claims eligible quotes:', error);
      toast.error('Failed to load claims data');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit]);

  const fetchStatistics = useCallback(async () => {
    try {
      const data = await apiClient.getClaimsStatistics();
      setStatistics(data);
    } catch (error) {
      console.error('Error fetching claims statistics:', error);
    }
  }, []);

  useEffect(() => {
    fetchQuotes();
    fetchStatistics();
  }, [fetchQuotes, fetchStatistics]);

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    fetchQuotes(1, value);
  }, [fetchQuotes]);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const columns: any[] = [
    {
      accessorKey: 'quoteNumber',
      header: 'Policy Number',
      enableSorting: true,
    },
    {
      accessorKey: 'productName',
      header: 'Product',
      enableSorting: true,
    },
    {
      accessorKey: 'participantCount',
      header: 'Participants',
      enableSorting: true,
    },
    {
      accessorKey: 'coverageStartDate',
      header: 'Coverage Start',
      enableSorting: true,
      cell: ({ getValue }: any) => {
        const date = getValue();
        return date ? new Date(date).toLocaleDateString() : '-';
      },
    },
    {
      accessorKey: 'coverageEndDate',
      header: 'Coverage End',
      enableSorting: true,
      cell: ({ getValue }: any) => {
        const date = getValue();
        return date ? new Date(date).toLocaleDateString() : '-';
      },
    },
    {
      accessorKey: 'sumInsured',
      header: 'Sum Insured',
      enableSorting: true,
      cell: ({ getValue }: any) => {
        const value = getValue();
        return value ? `$${Number(value).toLocaleString()}` : '-';
      },
    },
    {
      accessorKey: 'lastAssessmentDate',
      header: 'Last Assessment',
      cell: ({ getValue }: any) => {
        const date = getValue();
        if (!date) return <span className="text-base-content/50">Never</span>;
        return new Date(date).toLocaleDateString();
      },
    },
    {
      accessorKey: 'hasAssessments',
      header: 'Status',
      cell: ({ getValue }: any) => {
        const hasAssessments = getValue();
        return hasAssessments 
          ? <Badge variant="default">Assessed</Badge>
          : <Badge variant="secondary">Pending</Badge>;
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }: any) => (
        <PositiveButton
          onClick={() => handleView(row.original)}
          size="sm"
          showIcon={false}
        >
          <EyeIcon size={14} />
          View
        </PositiveButton>
      ),
    },
  ];
  /* eslint-enable @typescript-eslint/no-explicit-any */

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangleIcon size={28} className="text-warning" />
          <div>
            <h1 className="text-2xl font-bold text-base-content">Claims Assessment</h1>
            <p className="text-base-content/70">Manage claims for active policies</p>
          </div>
        </div>
        <NeutralButton
          onClick={() => {
            fetchQuotes(pagination.page, searchQuery);
            fetchStatistics();
          }}
          className="flex items-center gap-2"
        >
          <RefreshCwIcon size={16} />
          Refresh
        </NeutralButton>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-base-content/70">
                Calculated Claims
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-base-content">
                {statistics.assessments.calculated}
              </div>
              <p className="text-xs text-base-content/50">
                ${statistics.payoutAmounts.calculated.toLocaleString()}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-base-content/70">
                Approved Claims
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-success">
                {statistics.assessments.approved}
              </div>
              <p className="text-xs text-base-content/50">
                ${statistics.payoutAmounts.approved.toLocaleString()}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-base-content/70">
                Paid Out
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-info">
                {statistics.assessments.paidOut}
              </div>
              <p className="text-xs text-base-content/50">
                ${statistics.payoutAmounts.paidOut.toLocaleString()}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-base-content/70">
                Policies with Claims
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-base-content">
                {statistics.quotesWithClaims}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50" size={18} />
          <TextInput
            placeholder="Search by policy number..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={quotes}
        columns={columns}
        enableSorting={true}
        enableResizing={true}
        loading={loading}
        manualPagination={true}
        pageCount={pagination.totalPages}
        rowCount={pagination.total}
        onPaginationChange={(paginationState) => {
          const newPage = paginationState.pageIndex + 1;
          fetchQuotes(newPage, searchQuery);
        }}
        state={{
          pagination: {
            pageIndex: pagination.page - 1,
            pageSize: pagination.limit,
          },
        }}
        pagination={{
          showSizeSelector: true,
          showPageInfo: true,
          showNavigation: true,
          pageSizeOptions: [10, 25, 50, 100],
        }}
      />
    </div>
  );
}
